const AUTH_TRACE_KEY = "dc_auth_trace";
const MAX_AUTH_TRACE_EVENTS = 40;

export type AuthTraceValue = string | number | boolean | null;

export type AuthTraceEvent = {
  at: string;
  name: string;
  details: Record<string, AuthTraceValue>;
};

function detailsKey(details: Record<string, AuthTraceValue>): string {
  return JSON.stringify(
    Object.entries(details).sort(([left], [right]) => left.localeCompare(right))
  );
}

function normalizeValue(value: unknown): AuthTraceValue {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value;
  return null;
}

function safeReadTrace(): AuthTraceEvent[] {
  try {
    const raw = sessionStorage.getItem(AUTH_TRACE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is AuthTraceEvent => {
      return (
        typeof item === "object" &&
        item !== null &&
        typeof (item as AuthTraceEvent).at === "string" &&
        typeof (item as AuthTraceEvent).name === "string" &&
        typeof (item as AuthTraceEvent).details === "object" &&
        (item as AuthTraceEvent).details !== null
      );
    });
  } catch {
    return [];
  }
}

export function readAuthTrace(): AuthTraceEvent[] {
  return safeReadTrace();
}

export function clearAuthTrace(): void {
  try {
    sessionStorage.removeItem(AUTH_TRACE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

export function recordAuthTrace(name: string, details: Record<string, unknown> = {}): void {
  try {
    const normalizedDetails = Object.fromEntries(
      Object.entries(details).map(([key, value]) => [key, normalizeValue(value)])
    );
    const current = safeReadTrace();
    const previous = current[current.length - 1];
    if (previous?.name === name && detailsKey(previous.details) === detailsKey(normalizedDetails)) {
      return;
    }
    const next = [
      ...current,
      { at: new Date().toISOString(), name, details: normalizedDetails },
    ].slice(-MAX_AUTH_TRACE_EVENTS);
    sessionStorage.setItem(AUTH_TRACE_KEY, JSON.stringify(next));
  } catch {
    // Debug trace must never affect auth behavior.
  }
}
